import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Ban, Check, Edit, Eye, Plus, Trash2 } from 'lucide-react';
import { Status } from '@pillar-portal/database/dist/frontend';
import type { PermissionsBundlePublic } from '@pillar-portal/api-types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { StatusBadge } from '@/components/ui/status-badge';
import { PolicyPermissionBundleModal } from '@/components/permissions/PolicyPermissionBundleModal';
import { permissionsApi } from '@/lib/pillarApi';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { runInBackground } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';

export const PermissionPolicyTemplateOpaqueCursorSchema: z.ZodType<string> = z
    .string()
    .trim()
    .min(1);

type PermissionPolicyTemplateModalMode = 'create' | 'edit' | 'view';

function parseOpaqueCursorParam(pageParam: unknown): string | undefined {
    if (pageParam === null || pageParam === undefined) {
        return undefined;
    }
    const parseResult = PermissionPolicyTemplateOpaqueCursorSchema.safeParse(pageParam);
    if (!parseResult.success) {
        throw new Error('Expected opaque cursor');
    }
    return parseResult.data;
}

function formatPermissionCount(template: PermissionsBundlePublic): string {
    return template.permissions.length === 1
        ? '1 permission'
        : `${template.permissions.length} permissions`;
}

export default function PermissionPolicyTemplatesPage() {
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const queryKeyPrefix: readonly unknown[] = useMemo(
        () => buildRoleScopedQueryKey(activeRoleAssignmentId, ['permissionPolicyTemplates']),
        [activeRoleAssignmentId]
    );
    const [modalMode, setModalMode] = useState<PermissionPolicyTemplateModalMode | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<PermissionsBundlePublic | null>(null);
    const [pendingDeleteTemplate, setPendingDeleteTemplate] =
        useState<PermissionsBundlePublic | null>(null);

    const invalidateTemplates = (): void => {
        runInBackground(queryClient.invalidateQueries({ queryKey: queryKeyPrefix }), () => {});
    };

    const setActiveMutation = useMutation({
        mutationFn: async (params: { permissionsBundleUuid: string; isActive: boolean }) => {
            await permissionsApi.updateBundle(params.permissionsBundleUuid, {
                status: params.isActive ? Status.ACTIVE : Status.DISABLED,
            });
        },
        onSuccess: invalidateTemplates,
    });

    const deleteMutation = useMutation({
        mutationFn: async (bundleUuid: string) => {
            await permissionsApi.deleteBundle(bundleUuid);
        },
        onSuccess: () => {
            setPendingDeleteTemplate(null);
            invalidateTemplates();
        },
    });

    const mutationError: Error | null =
        setActiveMutation.error instanceof Error
            ? setActiveMutation.error
            : deleteMutation.error instanceof Error
              ? deleteMutation.error
              : null;

    return (
        <>
            <InfiniteScrollList<PermissionsBundlePublic, string, readonly unknown[]>
                pageTitle="Permission Policy Templates"
                cardTitle="Permission Policy Templates"
                cardId="permission-policy-templates-card"
                queryPlaceholder="Search permission policy templates..."
                queryKeyPrefix={queryKeyPrefix}
                pageSize={50}
                fetchPage={async (params) => {
                    const response = await permissionsApi.searchSharedBundles({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                    });
                    return {
                        items: response.bundles,
                        pagination: response.pagination,
                    };
                }}
                parseCursorParam={parseOpaqueCursorParam}
                stateCopy={{
                    loadingLabel: 'Loading permission policy templates...',
                    emptyLabel: 'No permission policy templates found.',
                    endOfListLabel: 'End of permission policy templates.',
                    genericErrorLabel: 'Failed to load permission policy templates.',
                }}
                headerActions={
                    <Button
                        type="button"
                        onClick={() => {
                            setSelectedTemplate(null);
                            setModalMode('create');
                        }}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Create Template
                    </Button>
                }
                controls={
                    mutationError === null ? null : (
                        <Alert variant="destructive">
                            <AlertDescription>{mutationError.message}</AlertDescription>
                        </Alert>
                    )
                }
                controlsPlacement="above-search"
                renderItems={({ items }) => (
                    <div className="space-y-3">
                        {items.map((template: PermissionsBundlePublic) => (
                            <div
                                key={template.permissions_bundle_uuid}
                                className="rounded-lg border p-4 transition-colors hover:bg-gray-50"
                            >
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1 space-y-2">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h3 className="font-semibold">{template.name}</h3>
                                            <StatusBadge status={template.status} />
                                        </div>
                                        {template.description !== null &&
                                        template.description.length > 0 ? (
                                            <p className="text-sm text-muted-foreground">
                                                {template.description}
                                            </p>
                                        ) : null}
                                        <p className="text-sm text-muted-foreground">
                                            {formatPermissionCount(template)}
                                        </p>
                                        {template.permissions_deserialization_error !==
                                        undefined ? (
                                            <p className="text-sm text-destructive">
                                                Invalid persisted permissions payload.
                                            </p>
                                        ) : null}
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            title="View template"
                                            onClick={() => {
                                                setSelectedTemplate(template);
                                                setModalMode('view');
                                            }}
                                        >
                                            <Eye className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            title="Edit template"
                                            onClick={() => {
                                                setSelectedTemplate(template);
                                                setModalMode('edit');
                                            }}
                                        >
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            title={
                                                template.status === Status.ACTIVE
                                                    ? 'Disable template'
                                                    : 'Enable template'
                                            }
                                            onClick={() => {
                                                setActiveMutation.mutate({
                                                    permissionsBundleUuid:
                                                        template.permissions_bundle_uuid,
                                                    isActive: template.status !== Status.ACTIVE,
                                                });
                                            }}
                                            disabled={setActiveMutation.isPending}
                                        >
                                            {template.status === Status.ACTIVE ? (
                                                <Ban className="h-4 w-4" />
                                            ) : (
                                                <Check className="h-4 w-4" />
                                            )}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            title="Delete template"
                                            className="text-red-600 hover:text-red-700"
                                            onClick={() => {
                                                setPendingDeleteTemplate(template);
                                            }}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            />

            <PolicyPermissionBundleModal
                open={modalMode !== null}
                onOpenChange={(open) => {
                    if (open) {
                        return;
                    }
                    setModalMode(null);
                    setSelectedTemplate(null);
                }}
                mode={modalMode ?? 'view'}
                organizationId={null}
                organizationUuid={null}
                organizationName="target organization"
                bundle={modalMode === 'create' ? null : selectedTemplate}
                allowScopeNeutralWildcard
                copy={{
                    createTitle: 'Create Permission Policy Template',
                    editTitle: 'Edit Permission Policy Template',
                    viewTitle: 'View Permission Policy Template',
                    createDescription:
                        'Create a reusable permission policy template with typed permissions.',
                    editDescription:
                        'Edit this reusable permission policy template and its typed permissions.',
                    viewDescription:
                        'View this reusable permission policy template and its typed permissions.',
                    deletedDescription: 'Review this deleted permission policy template.',
                    emptyPermissionsLabel: 'No permissions in this template.',
                    deleteConfirmationMessage: 'Delete this permission policy template?',
                    removePermissionConfirmationMessage:
                        'Remove this permission from the template?',
                    namePlaceholder: 'Cashier template',
                    wildcardSourceLabel: 'All resources of this type in the target organization',
                }}
                createBundle={async (request) => await permissionsApi.createSharedBundle(request)}
                updateBundle={async (bundleUuid, request) =>
                    await permissionsApi.updateBundle(bundleUuid, request)
                }
                deleteBundle={async (bundleUuid) => {
                    await permissionsApi.deleteBundle(bundleUuid);
                }}
                restoreBundle={async (bundleUuid) =>
                    await permissionsApi.updateBundle(bundleUuid, {
                        status: Status.ACTIVE,
                    })
                }
                onMutationSuccess={invalidateTemplates}
            />

            <ConfirmationModal
                open={pendingDeleteTemplate !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setPendingDeleteTemplate(null);
                    }
                }}
                message={
                    pendingDeleteTemplate === null
                        ? 'Delete this permission policy template?'
                        : `Delete permission policy template "${pendingDeleteTemplate.name}"?`
                }
                confirmLabel="Delete"
                onConfirm={async () => {
                    const templateToDelete = pendingDeleteTemplate;
                    if (templateToDelete === null) {
                        return;
                    }
                    await deleteMutation.mutateAsync(templateToDelete.permissions_bundle_uuid);
                }}
                onCancel={() => {
                    setPendingDeleteTemplate(null);
                }}
                isLoading={deleteMutation.isPending}
            />
        </>
    );
}
